import React, { useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Client } from "@/lib/types/database";
import type { User } from "@/lib/types/user";
import { Plus } from "lucide-react";
import { RecipientChips } from "./ClientEmailSelector/RecipientChips";
import { RecipientDropdown } from "./ClientEmailSelector/RecipientDropdown";
import { isValidEmail } from "./utils/email-validation";
import {
  syncTemplateVariablesToForm,
  updateFormFieldWithRecipients,
} from "./utils/template-variable-sync";
import type { ComposeFormValues } from "@/lib/types/compose-modal-types";
import { useRecipientActions } from "./ClientEmailSelector/useRecipientActions";

export interface EmailRecipient {
  id: string;
  type: "client" | "email" | "hub_user";
  email: string;
  name?: string;
  client?: Client;
  hubUser?: User;
}

type ComposeFormFieldName = keyof ComposeFormValues;

/**
 * Module-scope empties: a `= []` default allocates a NEW array per render, and
 * `prefilledEmails` is a `useEffect` dependency — a fresh one re-runs the effect
 * on every render (MEMO_STABLE_PROPS).
 */
const EMPTY_EMAILS: string[] = [];
const EMPTY_HUB_USERS: User[] = [];

export interface ClientEmailSelectorProps {
  form: UseFormReturn<ComposeFormValues>;
  fieldName: ComposeFormFieldName;
  label: string;
  placeholder?: string;
  helperText?: string;
  clientOptions: Client[];
  clientsLoading: boolean;
  isSearching: boolean;
  clientSearchTerm: string;
  onClientSearchChange: (term: string) => void;
  selectedRecipients: EmailRecipient[];
  onRecipientsChange: (recipients: EmailRecipient[]) => void;
  prefilledEmails?: string[];
  disabled?: boolean;
  /** Hub users from app_users table (optional - used in compose) */
  filteredHubUsers?: User[];
  hubUsersLoading?: boolean;
}

export function ClientEmailSelector({
  form,
  fieldName,
  label,
  placeholder = "Search clients or enter email addresses...",
  helperText,
  clientOptions,
  clientsLoading,
  isSearching,
  clientSearchTerm,
  onClientSearchChange,
  selectedRecipients,
  onRecipientsChange,
  prefilledEmails = EMPTY_EMAILS,
  disabled = false,
  filteredHubUsers = EMPTY_HUB_USERS,
  hubUsersLoading = false,
}: ClientEmailSelectorProps) {
  const [inputValue, setInputValue] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  // Anchor the recipient dropdown in a body portal so it escapes the compose
  // form's `overflow-y-auto` scroll body (ComposeFormTabs) that would otherwise
  // clip it. See Z_INDEX_AND_OVERFLOW.
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const updateMenuPosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 8, left: rect.left, width: rect.width });
  }, []);

  React.useEffect(() => {
    if (!isDropdownOpen) return;
    updateMenuPosition();
    const handle = () => updateMenuPosition();
    window.addEventListener("scroll", handle, true);
    window.addEventListener("resize", handle);
    return () => {
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle);
    };
  }, [isDropdownOpen, updateMenuPosition]);

  const { addClientRecipient, addHubUserRecipient, addEmailRecipient, removeRecipient } = useRecipientActions(
    selectedRecipients,
    onRecipientsChange,
    setInputValue,
    setIsDropdownOpen
  );

  const filteredClients = useMemo(() => {
    const searchTerm = inputValue.trim();
    if (!searchTerm) return clientOptions.slice(0, 10);
    return clientOptions
      .filter(
        (client) =>
          client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .slice(0, 10);
  }, [clientOptions, inputValue]);

  const handleInputKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        const value = inputValue.trim();
        if (value) {
          if (isValidEmail(value)) {
            addEmailRecipient(value);
          } else {
            onClientSearchChange(value);
            setIsDropdownOpen(true);
          }
        }
      } else if (e.key === "Backspace" && !inputValue && selectedRecipients.length > 0) {
        const lastRecipient = selectedRecipients[selectedRecipients.length - 1];
        removeRecipient(lastRecipient.id);
      }
    },
    [inputValue, selectedRecipients, addEmailRecipient, onClientSearchChange, removeRecipient]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);
      onClientSearchChange(value);
      setIsDropdownOpen(value.length > 0);
      setTimeout(() => {
        if (inputRef.current && document.activeElement !== inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    },
    [onClientSearchChange]
  );

  React.useEffect(() => {
    if (prefilledEmails.length > 0 && selectedRecipients.length === 0) {
      const prefilledRecipients: EmailRecipient[] = prefilledEmails.map((email, index) => ({
        id: `prefilled-${index}`,
        type: "email" as const,
        email,
      }));
      onRecipientsChange(prefilledRecipients);
    }
  }, [prefilledEmails, selectedRecipients.length, onRecipientsChange]);

  React.useEffect(() => {
    syncTemplateVariablesToForm(inputValue, form, fieldName);
  }, [inputValue, form, fieldName]);

  React.useEffect(() => {
    updateFormFieldWithRecipients(selectedRecipients, form, fieldName, inputValue);
  }, [selectedRecipients, form, fieldName, inputValue]);

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <div className="space-y-2">
              <RecipientChips recipients={selectedRecipients} onRemove={removeRecipient} />

              <div className="relative" ref={anchorRef}>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <Input
                      ref={inputRef}
                      placeholder={placeholder}
                      value={inputValue}
                      onChange={handleInputChange}
                      onFocus={() => !disabled && setIsDropdownOpen(true)}
                      onKeyDown={handleInputKeyPress}
                      onBlur={(e) => {
                        const relatedTarget = e.relatedTarget as HTMLElement;
                        if (relatedTarget && !relatedTarget.closest("[data-popover-content]")) {
                          setTimeout(() => {
                            if (document.activeElement !== inputRef.current) {
                              setIsDropdownOpen(false);
                            }
                          }, 200);
                        }
                      }}
                      disabled={disabled}
                      className="w-full"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={disabled}
                    onClick={() => {
                      setIsDropdownOpen((prev) => !prev);
                      if (!isDropdownOpen && inputRef.current) {
                        inputRef.current.focus();
                      }
                    }} title="Add"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {isDropdownOpen && menuPos && typeof document !== "undefined" &&
                  createPortal(
                    <div
                      // z-max: portalled to <body>, so it must clear the compose
                      // ResponsiveModal / Mantine modal (z-200) it opens over.
                      className="z-max rounded-md border bg-popover p-2 shadow-md"
                      data-popover-content
                      style={{
                        position: "fixed",
                        top: menuPos.top,
                        left: menuPos.left,
                        width: menuPos.width,
                      }}
                    >
                      <RecipientDropdown
                        inputValue={inputValue}
                        allClients={clientOptions}
                        filteredClients={filteredClients}
                        clientsLoading={clientsLoading}
                        isSearching={isSearching}
                        filteredHubUsers={filteredHubUsers}
                        hubUsersLoading={hubUsersLoading}
                        selectedRecipients={selectedRecipients}
                        onAddClient={addClientRecipient}
                        onAddHubUser={addHubUserRecipient}
                        onAddEmail={addEmailRecipient}
                        inputRef={inputRef}
                      />
                    </div>,
                    document.body,
                  )}
              </div>
              {helperText ? <p className="text-xs text-muted-foreground">{helperText}</p> : null}
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
